<template>
    <ListLayout form-title="Referrals" :has-list-scroll="false">
        <template #list-header>
            <div class="input-group search-container">
                <input
                    type="text"
                    class="form-control border-end-0"
                    placeholder="Search Referrals by Patient Name"
                    v-model="term"
                />
                <span class="input-group-append">
                    <div class="input-group-text bg-transparent p-2" @click="search()">
                        <i class="fas fa-search font-size-sm"></i>
                    </div>
                </span>
            </div>
        </template>
        <template #list-header-drop>
            <div class="me-0 ms-lg-4">
                <MultiSelect
                    :drop-down-options="agencyMultiSelectOptions"
                    :selected-options="selectedAgencies"
                    v-on:updateBranches="updateBranchesbyAgency"
                    label-text="Organizations"
                />
            </div>
            <div class="me-auto ms-lg-4">
                <BranchDefaultFilter
                    :check-all-by-default="true"
                    :selected-branches="selectedBranches"
                    v-on:updateBranches="updateBranches"
                    :selected-agencies-name="selectedAgenciesName"
                    :is-referral="true"
                />
            </div>
        </template>
        <template #list-header-new>
            <button
                v-if="excelExportPermission"
                type="button"
                class="btn btn-sm btn-primary ms-lg-2"
                @click="downloadChart()"
            >
                Export to Excel
            </button>
            <button type="button" class="btn btn-sm btn-primary ms-lg-2" @click="newReferral()">New</button>
        </template>
        <Pagination
            ref="referralListPagination"
            :pageChanged="pageChangedCallback"
            :isLoading="isLoading"
            entity="Referrals"
            :showPageSizeOptions="true"
            classes="pagination-footer"
            :pageSize="pageSize"
            v-on:updatePageSizeChanged="updatePageSizeChanged"
        >
            <StaticTableFormater
                :enabled="true"
                :isLoading="!(!isLoading && hasData)"
                :backColumnCount="1"
                :frontColumnCount="4"
                :tableColumnSettings="getCustomSettings()"
                :columnsAltered="dynamicTableToggled"
                v-model:columns="columns"
            >
                <table class="table table-responsive-md border border-gray">
                    <thead>
                        <tr class="bg-table-header table-header">
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="referralDate"
                                    >Referral Date
                                </SortableTh>
                            </th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="requestedStartOfCareDate"
                                    >Requested SOC
                                </SortableTh>
                            </th>
                            <th v-else>Referral Name</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="lastName"
                                >
                                    Patient Information
                                </SortableTh>
                            </th>
                            <th v-else>Patient Information</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="branchName"
                                >
                                    Branch
                                </SortableTh>
                            </th>
                            <th v-else>Branch</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="lineOfService"
                                >
                                    Line of Service
                                </SortableTh>
                            </th>
                            <th v-else>Line of Service</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="referralSource"
                                >
                                    Referral Type
                                </SortableTh>
                            </th>
                            <th v-else>Referral Type</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="dateOfBirth"
                                >
                                    Date of Birth
                                </SortableTh>
                            </th>
                            <th v-else>Date of Birth</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="gender"
                                >
                                    Sex
                                </SortableTh>
                            </th>
                            <th v-else>Sex</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="createdBy"
                                >
                                    Created By
                                </SortableTh>
                            </th>
                            <th v-else>Created By</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="liaison"
                                >
                                    Liaison
                                </SortableTh>
                            </th>
                            <th v-else>Liaison</th>
                            <th v-if="hasData">
                                <SortableTh
                                    :sort-by="sortBy"
                                    v-on:sortByUpdated="sortByUpdated"
                                    :sort-order="sortOrder"
                                    v-on:sortOrderUpdated="sortOrderUpdated"
                                    sort-string="tags"
                                >
                                    Tags
                                </SortableTh>
                            </th>
                            <th v-else>Tags</th>
                            <th class="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            class="table-empty"
                            v-if="!isLoading && !isError && !hasData && !branchRef?.isRequestPending"
                        >
                            <td :colspan="columns">
                                <h3>No Referrals Found.</h3>
                            </td>
                        </tr>

                        <template v-if="isLoading || branchRef?.isRequestPending">
                            <tr v-for="i in loadingCount" class="table-loading" :key="`count-${i}`">
                                <td v-for="i in columns" :key="`column-${i}`">
                                    <span class="shimmer-loading"></span>
                                </td>
                            </tr>
                        </template>

                        <tr class="table-error" v-if="!isLoading && isError && !branchRef?.isRequestPending">
                            <td :colspan="columns">
                                <ErrorMessageHandler
                                    title="Referral list"
                                    :show-try-again="true"
                                    :try-again="search()"
                                />
                            </td>
                        </tr>
                    </tbody>
                    <template v-if="!isLoading && !isError && hasData && !branchRef?.isRequestPending">
                        <tbody v-for="item in page.items" :key="item.id">
                            <CentralReferralRow
                                :referrals-editable="referralsEditable"
                                :referrals-deleteable="referralsDeleteable"
                                :referral="item"
                                :non-admit-reasons-data="nonAdmitReasonsData"
                                :columns="columns"
                                :branches="branches"
                                :line-of-service-enum-map="lineOfServiceEnumMap"
                                :get-gender-types="getGenderTypes"
                                :reset-list="resetList"
                                :refresh-list="pageRefresh"
                                :update-non-admit-reasons-data="updateNonAdmitReasonsData"
                                v-model:dynamicTableToggled="dynamicTableToggled"
                            />
                        </tbody>
                    </template>
                </table>
            </StaticTableFormater>
        </Pagination>
    </ListLayout>
</template>

<script lang="ts" src="./CentralReferral.ts"></script>
